import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { getUserSettings, updateUserSettings } from 'src/requests/settings';
import useAuth from 'src/hooks/useAuth';
import { useSnackbar } from 'notistack';

interface PrivacyModeContextValue {
  privacyMode: boolean;
  togglePrivacyMode: () => Promise<void>;
  privacyLoading: boolean;
}

const PrivacyModeContext = createContext<PrivacyModeContextValue>({
  privacyMode: false,
  togglePrivacyMode: async () => {
    throw new Error('You need to wrap your component in <PrivacyModeProvider>');
  },
  privacyLoading: false,
});

export const PrivacyModeProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [privacyMode, setPrivacyMode] = useState(false);
  const [privacyLoading, setPrivacyLoading] = useState(false);

  useEffect(() => {
    const fetchPrivacyMode = async () => {
      if (!user?.userId) return;
      setPrivacyLoading(true);
      try {
        const fetchUserSettings = await getUserSettings(user.userId);
        if (fetchUserSettings && fetchUserSettings.length) {
          const privacyModeSetting = fetchUserSettings.find(
            (setting) => setting.name === 'privacy'
          );
          if (privacyModeSetting && privacyModeSetting.value?.maskPII !== undefined) {
            setPrivacyMode(privacyModeSetting.value.maskPII);
          }
        }
      } catch (error) {
        console.error('Failed to fetch privacy mode:', error);
      } finally {
        setPrivacyLoading(false);
      }
    };

    fetchPrivacyMode();
  }, [user?.userId]);

  const togglePrivacyMode = async () => {
    if (!user?.userId) {
      enqueueSnackbar('No user ID available', { variant: 'error' });
      return;
    }

    const newPrivacyMode = !privacyMode;
    setPrivacyMode(newPrivacyMode);

    const result = await updateUserSettings(user.userId, {
      name: 'privacy',
      value: {
        maskPII: newPrivacyMode,
      },
    });

    if (result.error) {
      enqueueSnackbar('Failed to update privacy setting', { variant: 'error' });
      setPrivacyMode(!newPrivacyMode);
    }
  };

  return (
    <PrivacyModeContext.Provider
      value={{
        privacyMode,
        togglePrivacyMode,
        privacyLoading,
      }}
    >
      {children}
    </PrivacyModeContext.Provider>
  );
};

export const usePrivacyMode = (): PrivacyModeContextValue => useContext(PrivacyModeContext);
